import { useState } from "react";

export interface PurchaseOption {
  id: string;
  icon: string;
  label: string;
  subLabel: string;
  url: string;
  colorClass: string;
}

interface PurchaseOptionsEditorProps {
  options: PurchaseOption[];
  onChange: (options: PurchaseOption[]) => void;
}

const PRESET_OPTIONS = [
  { icon: "ri-amazon-line", label: "Buy on Amazon", subLabel: "Fast delivery available", colorClass: "from-orange-400 to-orange-500" },
  { icon: "ri-whatsapp-line", label: "Order on WhatsApp", subLabel: "Chat with us directly", colorClass: "from-green-400 to-green-500" },
  { icon: "ri-store-2-line", label: "Visit Our Store", subLabel: "Available in-store", colorClass: "from-pink-400 to-pink-500" },
  { icon: "ri-instagram-line", label: "Shop on Instagram", subLabel: "DM to order", colorClass: "from-pink-500 to-rose-500" },
  { icon: "ri-truck-line", label: "Bulk Orders", subLabel: "Corporate gifting", colorClass: "from-teal-400 to-cyan-500" },
  { icon: "ri-shopping-bag-line", label: "Buy on Flipkart", subLabel: "Delivery across India", colorClass: "from-yellow-400 to-orange-400" },
];

const COLOR_OPTIONS = [
  "from-orange-400 to-orange-500",
  "from-green-400 to-green-500",
  "from-pink-400 to-pink-500",
  "from-pink-500 to-rose-500",
  "from-teal-400 to-cyan-500",
  "from-yellow-400 to-orange-400",
  "from-red-400 to-red-500",
];

const ICON_OPTIONS = [
  "ri-amazon-line", "ri-whatsapp-line", "ri-store-2-line",
  "ri-instagram-line", "ri-truck-line", "ri-shopping-bag-line",
  "ri-phone-line", "ri-mail-line", "ri-global-line",
];

const emptyOption = (): PurchaseOption => ({
  id: Date.now().toString(),
  icon: "ri-store-2-line",
  label: "",
  subLabel: "",
  url: "",
  colorClass: "from-pink-400 to-pink-500",
});

const PurchaseOptionsEditor = ({ options, onChange }: PurchaseOptionsEditorProps) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  const addPreset = (preset: typeof PRESET_OPTIONS[0]) => {
    const newOpt: PurchaseOption = { ...preset, id: Date.now().toString(), url: "" };
    onChange([...options, newOpt]);
    setExpanded(newOpt.id);
  };

  const addCustom = () => {
    const newOpt = emptyOption();
    onChange([...options, newOpt]);
    setExpanded(newOpt.id);
  };

  const updateOption = (id: string, field: keyof PurchaseOption, value: string) => {
    onChange(options.map((o) => o.id === id ? { ...o, [field]: value } : o));
  };

  const removeOption = (id: string) => {
    onChange(options.filter((o) => o.id !== id));
    if (expanded === id) setExpanded(null);
  };

  const moveOption = (idx: number, dir: -1 | 1) => {
    const next = [...options];
    const to = idx + dir;
    if (to < 0 || to >= next.length) return;
    [next[idx], next[to]] = [next[to], next[idx]];
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Existing Options */}
      {options.length > 0 && (
        <div className="flex flex-col gap-2">
          {options.map((opt, idx) => (
            <div key={opt.id} className="border border-gray-100 rounded-xl overflow-hidden bg-gray-50">
              {/* Header row */}
              <div className="flex items-center gap-3 px-3 py-2.5">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${opt.colorClass} flex-shrink-0`}>
                  <i className={`${opt.icon} text-white text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{opt.label || "Untitled Option"}</p>
                  <p className="text-xs text-gray-400 truncate">{opt.url || "No URL set"}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {idx > 0 && (
                    <button type="button" onClick={() => moveOption(idx, -1)} className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-200 text-gray-400 cursor-pointer transition-colors">
                      <i className="ri-arrow-up-s-line text-xs" />
                    </button>
                  )}
                  {idx < options.length - 1 && (
                    <button type="button" onClick={() => moveOption(idx, 1)} className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-200 text-gray-400 cursor-pointer transition-colors">
                      <i className="ri-arrow-down-s-line text-xs" />
                    </button>
                  )}
                  <button type="button" onClick={() => setExpanded(expanded === opt.id ? null : opt.id)} className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-200 text-gray-500 cursor-pointer transition-colors">
                    <i className={`${expanded === opt.id ? "ri-arrow-up-s-line" : "ri-settings-3-line"} text-xs`} />
                  </button>
                  <button type="button" onClick={() => removeOption(opt.id)} className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-red-100 text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                    <i className="ri-delete-bin-6-line text-xs" />
                  </button>
                </div>
              </div>

              {/* Expanded editor */}
              {expanded === opt.id && (
                <div className="px-3 pb-3 pt-1 border-t border-gray-100 bg-white flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Label *</label>
                      <input type="text" value={opt.label} onChange={(e) => updateOption(opt.id, "label", e.target.value)} placeholder="e.g. Buy on Amazon" className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#3a8cbf] text-xs text-gray-800 outline-none" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Sub-label</label>
                      <input type="text" value={opt.subLabel} onChange={(e) => updateOption(opt.id, "subLabel", e.target.value)} placeholder="e.g. Fast delivery" className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#3a8cbf] text-xs text-gray-800 outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">URL *</label>
                    <input type="url" value={opt.url} onChange={(e) => updateOption(opt.id, "url", e.target.value)} placeholder="https://..." className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#3a8cbf] text-xs text-gray-800 outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Icon</label>
                      <select value={opt.icon} onChange={(e) => updateOption(opt.id, "icon", e.target.value)} className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#3a8cbf] text-xs text-gray-800 outline-none cursor-pointer">
                        {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic.replace("ri-", "").replace("-line", "")}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Color</label>
                      <select value={opt.colorClass} onChange={(e) => updateOption(opt.id, "colorClass", e.target.value)} className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 focus:border-[#3a8cbf] text-xs text-gray-800 outline-none cursor-pointer">
                        {COLOR_OPTIONS.map((c) => <option key={c} value={c}>{c.split(" ")[0].replace("from-", "")}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Quick Presets */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2">Quick add:</p>
        <div className="flex flex-wrap gap-2">
          {PRESET_OPTIONS.filter((p) => !options.find((o) => o.label === p.label)).map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => addPreset(preset)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:border-[#3a8cbf]/40 hover:bg-[#3a8cbf]/5 text-xs text-gray-600 hover:text-[#3a8cbf] cursor-pointer transition-all whitespace-nowrap"
            >
              <i className={`${preset.icon} text-xs`} />
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            onClick={addCustom}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-gray-300 bg-white hover:border-[#3a8cbf]/40 text-xs text-gray-400 hover:text-[#3a8cbf] cursor-pointer transition-all whitespace-nowrap"
          >
            <i className="ri-add-line text-xs" />
            Custom
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOptionsEditor;
